import React from 'react';
import { Linking } from 'react-native';
import { SheetProvider } from 'react-native-actions-sheet';
import PushNotification from 'react-native-push-notification';
import { connect } from 'react-redux';
import NetInfo from '@react-native-community/netinfo';
import { NavigationContainer } from '@react-navigation/native';

import AppUpdateModal from '../Components/AppUpdateModal';
import ConnectionIssueModal from '../Components/ConnectionIssueModal';
import DatePicker from '../Components/DatePicker';
import SchedulePicker from '../Components/SchedulePicker';
import useSchedulePicker from '../Components/SchedulePicker/index.controller';
import Toast from '../Components/Toast';

import '../Components/Sheets';

import config from '../Config';
import constants from '../Config/constants';
import { getPageName } from '../Helper/Routines';
import UrlParameterHelper from '../Helper/UrlParameter';
import useDeepLinking from '../Hooks/useDeepLinking';
import withController from '../Hooks/withController';
import withDatePicker from '../Hooks/withDatePicker';
import routeList from '../Routes/list';
import { isLoggedInSelector } from '../RTK/user/selectors';
import { setRoute } from '../RTK/utility';
import utilsApi from '../Service/api/utils';

import useRouteIndex from './index.controller';
import RootNavigator from './RootNavigator';

const webDeeplink = {
  [routeList.HOME]: {
    screens: {
      MainMenu: {
        screens: {
          [routeList.HOME_TAB]: routeList.HOME_TAB,
          [routeList.STORE]: routeList.STORE,
          [routeList.BASKET_TAB]: routeList.BASKET_TAB,
          [routeList.SEE_ALL]: routeList.SEE_ALL,
          [routeList.RESERVATION_TAB]: {
            screens: {
              [routeList.RESERVATION]: routeList.RESERVATION_TAB,
              [routeList.RES_STORE_DETAILS]: routeList.RES_STORE_DETAILS,
              [routeList.RES_EVENT_DETAILS]: routeList.RES_EVENT_DETAILS,
              [routeList.RES_CHECKOUT_ADDONS]: routeList.RES_CHECKOUT_ADDONS,
              [routeList.RES_CHECKOUT]: routeList.RES_CHECKOUT,
              [routeList.MY_RESERVATIONS]: routeList.MY_RESERVATIONS,
              [routeList.MY_RESERVATION_DETAILS]:
                routeList.MY_RESERVATION_DETAILS,
              [routeList.RES_STORE_DETAILS_BOOK]:
                routeList.RES_STORE_DETAILS_BOOK,
            },
          },
          [routeList.PROFILE]: routeList.PROFILE,
          [routeList.CHECKOUT]: routeList.CHECKOUT,
          [routeList.CHECKOUT_WEBVIEW]: routeList.CHECKOUT_WEBVIEW,
        },
      },
    },
  },
  [routeList.STORE_REVIEWS]: routeList.STORE_REVIEWS,
  [routeList.ORDER_DETAILS]: routeList.ORDER_DETAILS,
  [routeList.ORDER_RATING]: routeList.ORDER_RATING,
  [routeList.LEGAL_BINDING_CONTRACT]: routeList.LEGAL_BINDING_CONTRACT,
  [routeList.RES_EVENT_DETAILS_PREVIEW]: routeList.RES_EVENT_DETAILS_PREVIEW,
  [routeList.SIGNUP_VERIFICATION_SUCCESS]: 'verify-email',
};

const mobileDeepLink = {
  [routeList.ROOT_TAB]: {
    // root navigator
    screens: {
      [routeList.HOME_TAB]: {
        // home tab because navigator of store is in home navigator
        screens: {
          [routeList.STORE]: routeList.STORE,
        },
      },
      [routeList.BASKET_TAB]: {
        // basket tab because navigator of rating is in basket navigator
        screens: {
          [routeList.ORDER_RATING]: routeList.ORDER_RATING,
        },
      },
      [routeList.RESERVATION_TAB]: {
        // event tab because navigator
        screens: {
          [routeList.RESERVATION]: routeList.RESERVATION_TAB,
          [routeList.RES_STORE_DETAILS]: routeList.RES_STORE_DETAILS,
          [routeList.RES_EVENT_DETAILS]: routeList.RES_EVENT_DETAILS,
          [routeList.RES_STORE_DETAILS_BOOK]: routeList.RES_STORE_DETAILS_BOOK,
          [routeList.RES_EVENT_DETAILS_PREVIEW]:
            routeList.RES_EVENT_DETAILS_PREVIEW,
        },
      },
    },
  },
};

const mobilePublicDeepLink = {
  [routeList.RES_STORE_DETAILS_BOOK]: routeList.RES_STORE_DETAILS_BOOK,
  [routeList.RES_EVENT_DETAILS]: routeList.RES_EVENT_DETAILS,
  [routeList.RES_EVENT_DETAILS_PREVIEW]: routeList.RES_EVENT_DETAILS_PREVIEW,
  [routeList.SIGNUP_VERIFICATION_SUCCESS]: 'verify-email',
};

class Routes extends React.Component {
  pendingDeepLink = React.createRef();
  state = {
    isConnected: true,
    isInternetReachable: true,
    showAppUpdate: false,
  };

  componentDidMount() {
    this.shouldSetState = true;
    this.netInfoSubscription();
    this.updateChecker();
    if (constants.isWeb && !this.props.isLoggedIn) {
      // on web, we prepare deep linking on page mount
      this.props.deepLink.prepareDeepLinking(window.location.href);
    }
  }

  netInfoSubscription() {
    this.netInfoUnsubscribe?.();
    this.netInfoUnsubscribe = NetInfo.addEventListener(
      ({ isConnected, isInternetReachable }) => {
        this.setState({ isConnected, isInternetReachable });
      },
    );
  }

  async updateChecker() {
    const isUpdated = await utilsApi.isAppUpdated();

    if (this.shouldSetState && !isUpdated) {
      this.toggleAppUpdate();
    }
  }

  toggleAppUpdate = () => {
    this.setState({ showAppUpdate: !this.state.showAppUpdate });
  };

  componentWillUnmount() {
    this.shouldSetState = false;
    this.netInfoUnsubscribe?.();
  }

  _getDeepLinks = () => {
    if (this.props.isLoggedIn || constants.isWeb) {
      return constants.isWeb ? webDeeplink : mobileDeepLink;
    } else {
      return mobilePublicDeepLink;
    }
  };

  _linking = {
    prefixes: ['pickupcustomerapp://', config.WEB],
    config: {
      screens: this._getDeepLinks(),
    },
    getInitialURL: async () => {
      PushNotification.popInitialNotification((notification) => {
        if (!notification) return;
        const { link = null } = notification?.data || {};
        link && Linking.openURL(link);
      });
      return Linking.getInitialURL().then((url) => {
        // list of routes base on mobile deep linking object return as ['store', 'order_rating', 'etc.']
        const mobilePublicRoutes = Object.values(mobilePublicDeepLink);
        const urlPath = UrlParameterHelper.getUrlStringPath(url);
        if (
          !constants.isWeb &&
          !this.props.isLoggedIn &&
          !mobilePublicRoutes.includes(urlPath)
        ) {
          // on mobile, we prepare deep linking here (this is not getting called on web)
          this.pendingDeepLink.current = url;
          this.props.deepLink.prepareDeepLinking(url);
          return null; // Prevent default deep link processing
        } else {
          return url; // Use default deep link process
        }
      });
    },
  };

  render() {
    const {
      nav,
      orderNumber,
      schedulePicker,
      shopData,
      showDatePicker,
      toggleDateTimePicker,
      whenFilter,
      onConfirm,
    } = this.props; // for date picker and redux shop data
    const { isConnected, isInternetReachable, showAppUpdate } = this.state;

    return (
      <NavigationContainer
        ref={nav.containerRef}
        linking={this._linking} // deep linking for user when they logged in
        documentTitle={{
          formatter: (options, route) =>
            getPageName(route?.name, shopData?.name, orderNumber),
        }}
        onStateChange={nav.setRoute}
        onReady={() => {
          // on guest, deep link not working immediately
          // so this block of code handle redirect to deep link
          // after the navigation gets ready. Get trigger after cold start of the app (fully closed then open)
          const currentRoute = nav.containerRef.current.getCurrentRoute();
          const pendingDeepLinkTargetRoute =
            UrlParameterHelper.getUrlStringPath(this.pendingDeepLink.current);
          const isCurrentlyOnAuthScreen = currentRoute.name === routeList.AUTH;
          const isDeepLinkForGuestOnly =
            pendingDeepLinkTargetRoute in mobilePublicDeepLink;
          if (isCurrentlyOnAuthScreen && isDeepLinkForGuestOnly) {
            this.props.deepLink.redirectDeepLinking(
              nav.containerRef.current,
              true,
            );
          }
          nav.setRoute();
        }}
      >
        <SheetProvider>
          <Toast />

          <ConnectionIssueModal
            isConnected={isConnected}
            isInternetReachable={isInternetReachable}
          />

          {!constants.isWeb && (
            <AppUpdateModal
              show={showAppUpdate}
              onClose={this.toggleAppUpdate}
            />
          )}

          <RootNavigator />

          {/* This should be the only DatePicker for mobile (if intent is open the date picker to change order date), because if not it will show multiple date picker */}
          {/* Just call toggleDateTimePicker on withDatePicker hooks/hoc to open DatePicker */}
          {!constants.isWeb && (
            <DatePicker
              open={showDatePicker}
              value={whenFilter?.value}
              onCancel={() => toggleDateTimePicker('cancel')}
              onConfirm={onConfirm}
            />
          )}

          {/* Used when rescheduling */}
          {schedulePicker.showReschedule && !constants.isWeb && (
            <SchedulePicker
              scheduleTitle="Select Reschedule"
              storeId={schedulePicker.rescheduleStoreId}
              show={schedulePicker.showReschedule}
              onConfirm={schedulePicker.rescheduleOnStorePage}
            />
          )}
        </SheetProvider>
      </NavigationContainer>
    );
  }
}

Routes = withDatePicker(Routes);
Routes = withController(Routes, useSchedulePicker, 'schedulePicker');
Routes = withController(Routes, useDeepLinking, 'deepLink');
Routes = withController(Routes, useRouteIndex, 'nav');

const mapStateToProps = (state) => ({
  isLoggedIn: isLoggedInSelector(state),
  orderNumber: state.checkout.trackOrder,
  shopData: state.shop.data,
});

const mapDispatchToProps = (dispatch) => ({
  dispatchRoute: (data) => dispatch(setRoute(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Routes);
